import React, { useEffect, useMemo, useState } from "react";
import spotify from "../assets/socialmedia/spotify.png";
import apple   from "../assets/socialmedia/applemusic.png";
import youtube from "../assets/socialmedia/youtube.png";
import youtubeMusic from "../assets/socialmedia/youtubemusic.webp";
import tidal from "../assets/socialmedia/tidal.png";
import amazonMusic from "../assets/socialmedia/amazonmusic.png";
import bandcamp from "../assets/socialmedia/bandcamp.png";




import "./SongLinks.css";

/**
 * Responsive pre‑save / streaming links component
 *
 * Props:
 *  - title                : string  – song title
 *  - coverArt             : string  – URL/path to the cover‑art image
 *  - releaseDate          : string  – ISO‑8601 timestamp (e.g. "2025‑05‑30T00:00:00Z")
 *  - spotifyLink          : string  – Spotify stream link (after release)
 *  - spotifyPresaveLink   : string  – Spotify pre‑save link (before release)
 *  - appleMusicLink       : string  – Apple Music stream link
 *  - appleMusicPresaveLink: string  – Apple pre‑add link
 
 *  - youtubeMusicLink     : string  – YouTube Music link
 */
export default function SongLinks({
  title = "Untitled Track",
  coverArt,
  releaseDate,
  spotifyLink,
  spotifyPresaveLink,
  appleMusicLink,
  appleMusicPresaveLink,
  youtubeMusicLink,
  youtubeLink,
  tidalLink,
  amazonMusicLink,
  bandcampLink
}) {
  /* ---------- release date & “now” clock ---------- */
  const release = useMemo(() => new Date(releaseDate), [releaseDate]);
  const [now, setNow] = useState(() => new Date());

  // tick every second
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  /* ---------- helper + memo‑ised time‑left object ---------- */
  function getTimeLeft(current, target) {
    const diff = Math.max(target - current, 0);

    const MS  = 1000;
    const MIN = 60 * MS;
    const H   = 60 * MIN;
    const D   = 24 * H;

    return {
      days    : Math.floor(diff / D),
      hours   : Math.floor((diff % D) / H),
      minutes : Math.floor((diff % H) / MIN),
      seconds : Math.floor((diff % MIN) / MS)
    };
  }

  const timeLeft = useMemo(() => getTimeLeft(now, release), [now, release]);
  const isPresave = now < release;

// util: turn 1 → 1st, 2 → 2nd, 3 → 3rd, 4 → 4th, …
const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const formattedRelease = useMemo(() => {
  const day   = release.getDate();                                        // 30
  const month = release.toLocaleString("en-US", { month: "long" });       // May
  return `${month} ${ordinal(day)}`;                                      // "May 30th"
}, [release]);

  /* ---------- streaming / pre‑save links ---------- */
  const platforms = [
    {
      name: "Spotify",
      icon: spotify,
      link: spotifyLink,
      presaveLink: spotifyPresaveLink,
      presave: "Pre‑save on Spotify"
    },
    {
      name: "Apple Music",
      icon: apple,
      link: appleMusicLink,
      presaveLink: appleMusicPresaveLink,
      presave: "Pre‑add on Apple Music"
    },
    {
      name: "YouTube Music",
      icon: youtubeMusic,
      link: youtubeMusicLink
    },
    {
      name: "YouTube",
      icon: youtube,
      link: youtubeLink
    },
    {
      name: "Tidal",
      icon: tidal,
      link: tidalLink
    },
    {
      name: "Amazon Music",
      icon: amazonMusic,
      link: amazonMusicLink
    },
    {
      name: "Bandcamp",
      icon: bandcamp,
      link: bandcampLink
    }
  ].filter((p) => (isPresave ? p.presaveLink : p.link));

  /* ---------- JSX ---------- */
  return (
    <div className="all-links">
      {coverArt && (
        <div className="cover-art">
          <img src={coverArt} alt={`${title} cover art`} />
        </div>
      )}

      <br/>

      <div className="title">{title}</div>
      <div className="mondwild">MondWild</div>

  
      {isPresave &&
        <span className="release-date">Available on all platforms on {formattedRelease}</span>
      }
  
            {/* -------- Streaming / pre‑save links -------- */}
      <div className="link-list">
        {platforms.map((p) => (
          <div className="link-card" key={p.name}>
            <a
              href={isPresave ? p.presaveLink : p.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="link-card-elements">
                <div className="icon">
                  <img src={p.icon} alt={p.name} />
                </div>
                <div className="platform-link">
                  {isPresave ? p.presave : p.name}
                </div>
              </div>
            </a>
          </div>
        ))}
      </div>

      {/* -------- Countdown clock (before release) -------- */}
  
   
      {isPresave && (
        <div className="countdown">
          {[
            { value: timeLeft.days,    label: "Days"    },
            { value: timeLeft.hours,   label: "Hours"   },
            { value: timeLeft.minutes, label: "Minutes" },
            { value: timeLeft.seconds, label: "Seconds" }
          ].map((t, i, arr) => (
            <React.Fragment key={t.label}>
              <div className="countdown-unit">
                <span className="countdown-value">
                  {String(t.value).padStart(2, "0")}
                </span>
                <span className="countdown-label">{t.label}</span>
              </div>
              {i < arr.length - 1 && (
                <span className="countdown-separator">:</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      


    </div>
  );
}
